// Inline-SVG-Icons für den Dacheditor (Ribbon + Werkzeugpalette).
// currentColor, stroke-basiert — Größe via `size`-Prop. Bewusst dependency-frei
// (keine Icon-Library im Projekt vorhanden), passend zum pvXpert-Stil.
import type { CSSProperties } from "react";

interface IconProps {
  size?: number;
  style?: CSSProperties;
}

function svgProps(size: number, style?: CSSProperties) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 20 20",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    // Erzwingt feste Größe — als direktes Flex-Kind schrumpfte das SVG sonst auf
    // ~2px Breite (Flex min-content), wodurch die Icons unsichtbar wurden.
    style: { display: "block", flexShrink: 0, width: size, height: size, ...style },
  };
}

// ── Ribbon-Icons ─────────────────────────────────────────────────────────────

export const IconCheck = ({ size = 20, style }: IconProps) => (
  <svg {...svgProps(size, style)}>
    <path d="M4 10.5l4 4 8-9" />
  </svg>
);

export const IconImport = ({ size = 20, style }: IconProps) => (
  <svg {...svgProps(size, style)}>
    <path d="M10 3v9" />
    <path d="M6.5 8.5L10 12l3.5-3.5" />
    <path d="M4 15h12" />
  </svg>
);

export const IconReset = ({ size = 20, style }: IconProps) => (
  <svg {...svgProps(size, style)}>
    <path d="M5 10a5 5 0 1 1 1.6 3.7" />
    <path d="M5 6v4h4" />
  </svg>
);

export const IconTrash = ({ size = 20, style }: IconProps) => (
  <svg {...svgProps(size, style)}>
    <path d="M4 6h12" />
    <path d="M7.5 6V4.5h5V6" />
    <path d="M6 6l.8 9.5h6.4L14 6" />
  </svg>
);

export const IconImage = ({ size = 20, style }: IconProps) => (
  <svg {...svgProps(size, style)}>
    <rect x="3" y="4.5" width="14" height="11" rx="1.5" />
    <circle cx="7.5" cy="8.5" r="1.2" />
    <path d="M4 14l4-4 3 3 2.5-2.5L16 13" />
  </svg>
);

export const IconEye = ({ size = 20, style }: IconProps) => (
  <svg {...svgProps(size, style)}>
    <path d="M2.5 10S5.5 5 10 5s7.5 5 7.5 5-3 5-7.5 5-7.5-5-7.5-5z" />
    <circle cx="10" cy="10" r="2.2" />
  </svg>
);

export const IconCube = ({ size = 20, style }: IconProps) => (
  <svg {...svgProps(size, style)}>
    <path d="M10 3l6 3.2v7.6L10 17l-6-3.2V6.2L10 3z" />
    <path d="M4 6.2l6 3.2 6-3.2" />
    <path d="M10 9.4V17" />
  </svg>
);

export const IconPin = ({ size = 20, style }: IconProps) => (
  <svg {...svgProps(size, style)}>
    <path d="M10 17s5-4.2 5-8a5 5 0 0 0-10 0c0 3.8 5 8 5 8z" />
    <circle cx="10" cy="9" r="1.8" />
  </svg>
);

export const IconClose = ({ size = 16, style }: IconProps) => (
  <svg {...svgProps(size, style)}>
    <path d="M5 5l10 10M15 5L5 15" />
  </svg>
);

// ── Werkzeug-Icons ───────────────────────────────────────────────────────────

export const IconSelect = ({ size = 19, style }: IconProps) => (
  <svg {...svgProps(size, style)}>
    <path d="M5 4l9 4-4 1.5L8.5 14 5 4z" />
  </svg>
);

export const IconHand = ({ size = 19, style }: IconProps) => (
  <svg {...svgProps(size, style)}>
    <path d="M10 3v6" />
    <path d="M10 4l-3 3M10 4l3 3M10 16l-3-3M10 16l3-3M3 10h6M17 10h-6" />
  </svg>
);

export const IconRect = ({ size = 19, style }: IconProps) => (
  <svg {...svgProps(size, style)}>
    <rect x="3.5" y="5" width="13" height="10" rx="0.8" />
  </svg>
);

export const IconPolygon = ({ size = 19, style }: IconProps) => (
  <svg {...svgProps(size, style)}>
    <path d="M10 3l6.5 5-2.5 8H6L3.5 8 10 3z" />
  </svg>
);

export const IconRuler = ({ size = 19, style }: IconProps) => (
  <svg {...svgProps(size, style)}>
    <rect
      x="2.2"
      y="6.5"
      width="15.6"
      height="7"
      rx="1"
      transform="rotate(45 10 10)"
    />
    <path d="M8 7.5l1 1M10.5 5l1 1M5.5 10l1 1" />
  </svg>
);

export const IconAngle = ({ size = 19, style }: IconProps) => (
  <svg {...svgProps(size, style)}>
    <path d="M4 16h12L4 6v10z" />
    <path d="M9 16a5 5 0 0 0-5-5" />
  </svg>
);
